import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { GenerateLocationsPreview, GenerateLocationsRequest } from "@wms/domain";
import { Alert, Button, Checkbox, Col, Form, Input, InputNumber, Modal, Row, Space, Typography } from "antd";
import { useState } from "react";
import { api, describeError, fieldErrors } from "../api/client";
import { RefSelect, useRefRows } from "./RefSelect";

interface Props {
  open: boolean;
  rackId?: string;
  /** Racks and zones are offered only for this warehouse (Astana report: zones of another warehouse were listed). */
  warehouseId?: string;
  onClose: () => void;
}

type FormValues = Omit<GenerateLocationsRequest, "dry_run">;

const DEFAULT_TEMPLATE = "{стеллаж}-{ярус:2}-{позиция:2}";

/** The parts of an address in words, as the server reads them; the English names of earlier templates work too. */
const PARTS: Record<string, string> = { стеллаж: "rack", ярус: "level", позиция: "pos", секция: "pos", проход: "aisle", помещение: "room", зона: "zone", префикс: "prefix" };
const PART_BUTTONS: Array<{ word: string; part: string }> = [
  { word: "стеллаж", part: "{стеллаж}" },
  { word: "ярус", part: "{ярус:2}" },
  { word: "позиция", part: "{позиция:2}" },
  { word: "проход", part: "{проход}" },
  { word: "зона", part: "{зона}" },
  { word: "помещение", part: "{помещение}" },
  { word: "префикс типа", part: "{префикс}" },
];

/** The address the template gives for the first cell: «{стеллаж}-{ярус:2}» → «01-03». */
function exampleAddress(template: string, values: Record<string, string | number>): string {
  return template.replace(/\{([^{}:]+?)(?::(\d+)d?)?\}/g, (all, name: string, width?: string) => {
    const word = name.trim().toLowerCase().replace("ё", "е");
    const v = values[PARTS[word] ?? word];
    if (v === undefined) return all;
    return width ? String(v).padStart(Number(width), "0") : String(v);
  });
}

/** Wizard "Сгенерировать ячейки": racks, type, zone, ranges, template → preview → create. */
export function GenerateLocationsModal({ open, rackId, warehouseId, onClose }: Props) {
  const [form] = Form.useForm<FormValues>();
  const rooms = useRefRows(warehouseId ? "rooms" : undefined, undefined, warehouseId ? { field: "warehouse_id", op: "eq", value: warehouseId } : null);
  const roomIds = (rooms.data?.items ?? []).map((r) => String(r.id));
  const rackFilter = warehouseId ? { field: "room_id", op: "in", value: roomIds.length ? roomIds : ["00000000-0000-0000-0000-000000000000"] } : null;
  const zoneFilter = warehouseId ? { field: "warehouse_id", op: "eq", value: warehouseId } : null;
  const racks = useRefRows("racks", undefined, rackFilter);
  const zones = useRefRows("zones", undefined, zoneFilter);
  const aisles = useRefRows("aisles");
  const types = useRefRows("location_types");
  const template = Form.useWatch("template", form) ?? "";
  const rackIds = Form.useWatch("rack_ids", form) ?? [];
  const zoneId = Form.useWatch("zone_id", form);
  const typeId = Form.useWatch("type_id", form);
  const levelFrom = Form.useWatch("level_from", form) ?? 1;
  const positionFrom = Form.useWatch("position_from", form) ?? 1;
  const rack = racks.data?.items.find((r) => String(r.id) === String(rackIds[0]));
  const example = exampleAddress(template, {
    rack: String(rack?.code ?? "01"),
    level: levelFrom,
    pos: positionFrom,
    aisle: String(aisles.data?.items.find((a) => String(a.id) === String(rack?.aisle_id))?.code ?? "П1"),
    room: String(rooms.data?.items.find((r) => String(r.id) === String(rack?.room_id))?.code ?? "ЗАЛ"),
    zone: String(zones.data?.items.find((z) => String(z.id) === String(zoneId))?.code ?? "ХР"),
    prefix: String(types.data?.items.find((t) => String(t.id) === String(typeId))?.prefix ?? ""),
  });
  const addPart = (part: string) => {
    const current = form.getFieldValue("template") ?? "";
    form.setFieldValue("template", current && !/[-–_./ ]$/.test(current) ? `${current}-${part}` : `${current}${part}`);
    setPreview(null);
  };
  const queryClient = useQueryClient();
  const [preview, setPreview] = useState<GenerateLocationsPreview | null>(null);

  const run = useMutation({
    mutationFn: (dryRun: boolean) => api.post<GenerateLocationsPreview>("/locations/generate", { ...form.getFieldsValue(true), dry_run: dryRun }),
    onSuccess: (data) => {
      setPreview(data);
      if (!data.dry_run) {
        // BUG-Z-001: the plan, the tree, the zone readiness and the lists all show the new cells at once
        queryClient.invalidateQueries({ queryKey: ["locations"] });
        queryClient.invalidateQueries({ queryKey: ["topology"] });
      }
    },
    // FULL0211-B10: a refused preview or creation never leaves the old «Будет создано …» with «Создать» enabled; the
    // server names the template when its addresses repeat — the field is marked, everything typed stays
    onError: (e) => {
      setPreview(null);
      const atTemplate = fieldErrors(e).find((x) => x.field === "template");
      if (atTemplate) form.setFields([{ name: "template", errors: [atTemplate.message] }]);
    },
  });

  const submit = async (dryRun: boolean) => {
    await form.validateFields();
    run.mutate(dryRun);
  };

  const close = () => {
    setPreview(null);
    form.resetFields();
    onClose();
  };

  return (
    <Modal title="Генерация ячеек" open={open} onCancel={close} footer={null} width={760} destroyOnHidden>
      <Form
        form={form}
        layout="vertical"
        initialValues={{ rack_ids: rackId ? [rackId] : [], level_from: 1, level_to: 4, position_from: 1, position_to: 10, template: DEFAULT_TEMPLATE, serpentine: false }}
        onValuesChange={() => setPreview(null)}
      >
        <Row gutter={16}>
          <Col span={24}>
            <Form.Item name="rack_ids" label="Стеллажи" rules={[{ required: true, message: "Выберите стеллаж" }]}>
              <RefSelect entity="racks" multiple filter={rackFilter} emptyHint="На этом складе нет стеллажей — создайте помещение и стеллаж" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="type_id" label="Тип ячейки" rules={[{ required: true, message: "Выберите тип" }]}>
              <RefSelect entity="location_types" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="zone_id" label="Зона">
              <RefSelect entity="zones" filter={zoneFilter} emptyHint="На этом складе нет зон — создайте зону склада" />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item name="level_from" label="Ярус с">
              <InputNumber min={1} style={{ width: "100%" }} />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item name="level_to" label="Ярус по">
              <InputNumber min={1} style={{ width: "100%" }} />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item name="position_from" label="Позиция с">
              <InputNumber min={1} style={{ width: "100%" }} />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item name="position_to" label="Позиция по">
              <InputNumber min={1} style={{ width: "100%" }} />
            </Form.Item>
          </Col>
          <Col span={16}>
            <Form.Item
              name="template"
              label="Шаблон адреса"
              tooltip="Адрес ячейки собирается из частей в фигурных скобках: {стеллаж}, {ярус}, {позиция}, {проход}, {зона}, {помещение}, {префикс} — префикс типа ячейки. «:2» после названия — две цифры с нулём впереди (01, 02 … 10). Между частями — любые знаки, например дефис."
              rules={[{ required: true, message: "Задайте шаблон адреса" }]}
              extra={
                <>
                  <Space size={4} wrap style={{ margin: "6px 0 4px" }}>
                    <Typography.Text type="secondary">Добавить:</Typography.Text>
                    {PART_BUTTONS.map((b) => (
                      <Button key={b.part} size="small" onClick={() => addPart(b.part)}>
                        + {b.word}
                      </Button>
                    ))}
                    <Button size="small" type="link" onClick={() => {
                        form.setFieldValue("template", DEFAULT_TEMPLATE);
                        setPreview(null);
                      }}
                    >
                      как было
                    </Button>
                  </Space>
                  <div>
                    Первая ячейка получит адрес <Typography.Text strong>{example || "—"}</Typography.Text>
                  </div>
                </>
              }
            >
              <Input placeholder={DEFAULT_TEMPLATE} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="serpentine" valuePropName="checked" label=" ">
              <Checkbox>Обход змейкой</Checkbox>
            </Form.Item>
          </Col>
        </Row>
      </Form>
      {run.error && <Alert type="error" message={describeError(run.error)} showIcon style={{ marginBottom: 12 }} />}
      {preview && (
        <Alert
          type={preview.dry_run ? "info" : "success"}
          showIcon
          style={{ marginBottom: 12 }}
          message={preview.dry_run ? `Будет создано ${preview.total - preview.skipped} из ${preview.total}, уже существует ${preview.skipped}` : `Создано ${preview.created}, пропущено ${preview.skipped}`}
          description={<Typography.Text code>{preview.sample.join(", ")}{preview.total > preview.sample.length ? " …" : ""}</Typography.Text>}
        />
      )}
      <Space style={{ width: "100%", justifyContent: "flex-end" }}>
        <Button onClick={close}>Закрыть</Button>
        <Button onClick={() => submit(true)} loading={run.isPending && run.variables === true}>
          Предпросмотр
        </Button>
        <Button type="primary" disabled={!preview || !preview.dry_run} onClick={() => submit(false)} loading={run.isPending && run.variables === false}>
          Создать
        </Button>
      </Space>
    </Modal>
  );
}
